import React, { useEffect } from 'react';

import IncentiveBanner from '../../phone_calls/incentive_banner';

import styles from './index.module.css';

const PhoneIcon = () => (
  <svg viewBox='0 0 256 256' fill='currentColor' xmlns='http://www.w3.org/2000/svg' aria-hidden='true'>
    <path d='M231.88,175.08A56.26,56.26,0,0,1,176,224C96.6,224,32,159.4,32,80A56.26,56.26,0,0,1,80.92,24.12a16,16,0,0,1,16.62,9.52l21.12,47.15,0,.12A16,16,0,0,1,117.39,96c-.18.27-.37.52-.57.77L96,121.45c7.49,15.22,23.41,31,38.83,38.51l24.34-20.71a8.12,8.12,0,0,1,.75-.56,16,16,0,0,1,15.17-1.4l.13.06,47.11,21.11A16,16,0,0,1,231.88,175.08Z' />
  </svg>
);

const SparkleIcon = () => (
  <svg viewBox='0 0 256 256' fill='currentColor' xmlns='http://www.w3.org/2000/svg' aria-hidden='true'>
    <path d='M208,144a15.78,15.78,0,0,1-10.42,14.94l-51.65,19-19,51.61a15.92,15.92,0,0,1-29.88,0L78,178l-51.62-19a15.92,15.92,0,0,1,0-29.88L78,110l19-51.65a15.92,15.92,0,0,1,29.88,0l19,51.61,51.65,19A15.78,15.78,0,0,1,208,144ZM152,48h16V64a8,8,0,0,0,16,0V48h16a8,8,0,0,0,0-16H184V16a8,8,0,0,0-16,0V32H152a8,8,0,0,0,0,16Zm88,32h-8V72a8,8,0,0,0-16,0v8h-8a8,8,0,0,0,0,16h8v8a8,8,0,0,0,16,0V96h8a8,8,0,0,0,0-16Z' />
  </svg>
);

const CloseIcon = () => (
  <svg
    width='13'
    height='13'
    viewBox='0 0 256 256'
    fill='currentColor'
    xmlns='http://www.w3.org/2000/svg'
    aria-hidden='true'
  >
    <path d='M208.49,191.51a12,12,0,0,1-17,17L128,145,64.49,208.49a12,12,0,0,1-17-17L111,128,47.51,64.49a12,12,0,0,1,17-17L128,111l63.51-63.52a12,12,0,0,1,17,17L145,128Z' />
  </svg>
);

// Voice-call invitation card that renders in the normal layout flow just
// above the message composer, so it never covers any of the conversation —
// the message area simply makes room for it. It never dims the page, so the
// rest of the conversation stays interactive. Patients see this; mentors get
// a different prompt.
export default function FeatureCalloutOverlay({
  title = 'Ready for a voice call?',
  badgeLabel = 'New',
  body,
  incentive = null,
  mentorName,
  primaryLabel = 'Request a call',
  onPrimary,
  secondaryLabel = 'Schedule a call',
  onSecondary,
  onDismiss,
  closeAriaLabel = 'Dismiss',
  ariaLabelledBy = 'feature-callout-title',
}) {
  useEffect(() => {
    if (typeof onDismiss !== 'function') return undefined;
    const handler = (event) => {
      if (event.key === 'Escape') onDismiss();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onDismiss]);

  const handleDismiss = () => {
    if (typeof onDismiss === 'function') onDismiss();
  };

  const handlePrimary = () => {
    if (typeof onPrimary === 'function') onPrimary();
  };

  const handleSecondary = () => {
    if (typeof onSecondary === 'function') onSecondary();
  };

  return (
    <div className={styles.root} role='dialog' aria-modal='false' aria-labelledby={ariaLabelledBy}>
      <div className={styles.card}>
        <button type='button' className={styles.close} onClick={handleDismiss} aria-label={closeAriaLabel}>
          <CloseIcon />
        </button>

        <div className={styles.header}>
          <span className={styles.icon} aria-hidden='true'>
            <PhoneIcon />
          </span>
          <div className={styles.title_row}>
            <h2 id={ariaLabelledBy} className={styles.title}>
              {title}
            </h2>
            {badgeLabel && (
              <span className={styles.badge}>
                <SparkleIcon />
                {badgeLabel}
              </span>
            )}
          </div>
        </div>

        {body && <p className={styles.body}>{body}</p>}

        {incentive && <IncentiveBanner incentive={incentive} mentorName={mentorName} variant='callout' inline />}

        <div className={styles.actions}>
          <button type='button' className={styles.primary_btn} onClick={handlePrimary}>
            <PhoneIcon />
            {primaryLabel}
          </button>
          <button type='button' className={styles.secondary_btn} onClick={handleSecondary}>
            {secondaryLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
